/**
 * Select — custom single-select dropdown (Aurora-v3).
 *
 * Een native <select> kan z'n geopende option-lijst niet stylen (de
 * lijst is OS-gestuurd → blauwe highlight op Windows). Voor de Aurora-
 * look (rose hover/selected, ronde hoeken, schaduw, portal boven
 * overflow) gebruiken we een custom listbox — dezelfde machinerie als
 * de Combobox, maar single-value en zonder chips/zoekveld.
 *
 * API spiegelt Input (label + hint + error in dezelfde wrapper) zodat
 * formulieren visueel uniform zijn. Let op: `onChange` geeft direct de
 * waarde terug — `(value: string) => void` — niet een DOM-event, omdat
 * er geen native <select> meer onder zit.
 */
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { CaretDown, Check } from '@phosphor-icons/react';
import { useAnchoredDropdown } from '../useAnchoredDropdown';
import styles from './Select.module.css';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  label: string;
  /**
   * De naam voor hulpsoftware wanneer `label` leeg is (issue 551).
   *
   * LOKALE TOEVOEGING, niet in het package. Een `label=""` is toegestaan voor
   * een compacte keuze in een tabelrij, en dan heeft de knop geen enkele naam —
   * `role="combobox"` haalt hem niet uit zijn inhoud. Gebruik dit daar, of geef
   * een `placeholder` (die dient als terugval).
   */
  ariaLabel?: string;
  hint?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /**
   * LOKALE toevoeging (niet in de Aurora-spec): het label wél meegeven maar niet
   * tónen. Nodig in een dichte tabel met een veld per rij — daar staat de naam al
   * in de rijkop en de betekenis al in de kolomkop, en het label eronder herhaalt
   * dat bij elke cel. Weglaten mag niet: dan heeft het veld geen toegankelijke
   * naam. Gemeld bij het DS-team; zie AURORA-NOTES.md.
   */
  labelHidden?: boolean;
  id?: string;
  className?: string;
}

export function Select({
  label,
  ariaLabel,
  hint,
  error,
  options,
  placeholder,
  value,
  onChange,
  disabled,
  labelHidden,
  id: externalId,
  className,
}: SelectProps) {
  const generatedId = useId();
  const id = externalId ?? generatedId;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const listboxId = `${id}-listbox`;

  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLUListElement | null>(null);

  const selectedOption = options.find((o) => o.value === value);
  const describedBy = [hint && hintId, error && errorId].filter(Boolean).join(' ') || undefined;

  // Portal-positie t.o.v. de trigger (boven parent-overflow heen); volgt de scroll zonder React-render.
  const dropdownPos = useAnchoredDropdown(open, triggerRef, dropdownRef);

  // Highlight de huidige selectie bij openen.
  useEffect(() => {
    if (open) {
      const idx = options.findIndex((o) => o.value === value);
      setHighlighted(idx >= 0 ? idx : 0);
    }
  }, [open, options, value]);

  // Click-outside sluit (trigger + portal-dropdown buiten beschouwing).
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (!triggerRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function selectValue(v: string) {
    onChange(v);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onTriggerKey(e: KeyboardEvent<HTMLButtonElement>) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(options.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const opt = options[highlighted];
      if (opt && !opt.disabled) selectValue(opt.value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    } else if (e.key === 'Tab') {
      // Geen preventDefault — focus verlaat het veld gewoon; alleen de
      // dropdown dicht (WAI-ARIA listbox: Tab sluit zonder te selecteren).
      setOpen(false);
    }
  }

  const triggerClasses = [
    styles.trigger,
    error ? styles.triggerError : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.wrapper}>
      <label htmlFor={id} className={labelHidden ? styles.labelHidden : styles.label}>{label}</label>
      {/* ⚠️ EEN SELECT ZONDER ZICHTBAAR LABEL HEEFT GEEN NAAM (issue 551).
          `label` is verplicht, maar `label=""` is toegestaan en wordt in de
          praktijk gebruikt voor een compacte keuze in een tabelrij. Dan is er
          geen tekst om naar te wijzen, en omdat `role="combobox"` zijn naam
          **niet uit zijn inhoud** haalt, kondigt een schermlezer hem aan als een
          kale "combobox". Gemeten in de catalogus-lijst: twee per rij.

          `ariaLabel` is daarvoor, met de placeholder als terugval zodat het
          gewone geval vanzelf goed komt. Bij een wél gevuld `label` verandert er
          niets — dan doet `htmlFor` zijn werk.

          `labelHidden` lost hetzelfde probleem anders op, en is de betere van de
          twee waar hij kan: het label hóudt zijn tekst en wordt alleen niet
          getoond, dus de naam komt gewoon uit `htmlFor` — één naam in plaats van
          twee die uit de pas kunnen lopen. Gebruik `ariaLabel` alleen waar er
          echt geen tekst is om te tonen. */}
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className={triggerClasses}
        disabled={disabled}
        role="combobox"
        aria-label={label === '' ? (ariaLabel ?? placeholder) : undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKey}
      >
        <span className={selectedOption ? styles.value : styles.placeholder}>
          {selectedOption ? selectedOption.label : (placeholder ?? '')}
        </span>
        <span className={styles.caret} aria-hidden="true">
          <CaretDown size={16} weight="regular" />
        </span>
      </button>

      {open && dropdownPos &&
        createPortal(
          <ul
            ref={dropdownRef}
            id={listboxId}
            role="listbox"
            className={styles.dropdown}
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              bottom: dropdownPos.bottom,
              left: dropdownPos.left,
              width: dropdownPos.width,
              maxHeight: dropdownPos.maxHeight,
            }}
          >
            {options.map((opt, idx) => {
              const selected = opt.value === value;
              return (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={selected}
                  aria-disabled={opt.disabled || undefined}
                  className={[
                    styles.option,
                    idx === highlighted ? styles.optionHighlighted : '',
                    opt.disabled ? styles.optionDisabled : '',
                  ].filter(Boolean).join(' ')}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!opt.disabled) selectValue(opt.value);
                  }}
                  onMouseEnter={() => setHighlighted(idx)}
                >
                  <span className={styles.checkSlot} aria-hidden="true">
                    {selected && <Check size={14} weight="bold" />}
                  </span>
                  <span className={styles.optionLabel}>{opt.label}</span>
                </li>
              );
            })}
          </ul>,
          document.body,
        )}

      {hint && !error && <span id={hintId} className={styles.hint}>{hint}</span>}
      {error && <span id={errorId} className={styles.errorText} role="alert">{error}</span>}
    </div>
  );
}
